import { useEffect, useState } from "react";
import { Loader2, Phone, Mail, Calendar, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const STATUS = ["new", "scheduled", "completed", "cancelled"];
const COLORS = {
  new: "bg-blue-100 text-blue-700",
  scheduled: "bg-amber-100 text-amber-700",
  completed: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-stone-200 text-stone-600",
};

export const ViewingsTab = () => {
  const [items, setItems] = useState(null);

  const load = () => api.get("/admin/realestate/viewings").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/realestate/viewings/${id}`, { status });
      await load();
      toast.success(`Marked ${status}`);
    } catch { toast.error("Update failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this viewing request?")) return;
    try { await api.delete(`/admin/realestate/viewings/${id}`); await load(); toast.success("Deleted"); }
    catch { toast.error("Delete failed"); }
  };

  if (items === null) return <div className="flex min-h-[40vh] items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#C9A24B]" /></div>;

  return (
    <div data-testid="re-viewings-tab">
      <h2 className="font-serif text-2xl font-semibold text-[#1A1615]">Viewing Requests</h2>
      <p className="mt-1 text-xs text-[#91837C]">{items.length} total · {items.filter((i) => i.status === "new").length} new</p>
      <div className="mt-5 space-y-3">
        {items.map((v) => (
          <div key={v.id} className="glass rounded-2xl p-4" data-testid={`re-viewing-${v.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-[#1A1615]">{v.name}</p>
                <p className="mt-0.5 text-xs text-[#91837C]">
                  {v.listing_title || "General enquiry"}{v.listing_ref ? ` · ${v.listing_ref}` : ""}
                </p>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#5A504B]">
                  <a href={`tel:${v.phone}`} className="inline-flex items-center gap-1 hover:text-[#B38B2D]"><Phone className="h-3 w-3" /> {v.phone}</a>
                  {v.email && <a href={`mailto:${v.email}`} className="inline-flex items-center gap-1 hover:text-[#B38B2D]"><Mail className="h-3 w-3" /> {v.email}</a>}
                  {v.preferred_date && <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {v.preferred_date} {v.preferred_time}</span>}
                </div>
                {v.message && <p className="mt-2 rounded-lg bg-[#F8F3E6] px-3 py-2 text-xs text-[#5A504B]">{v.message}</p>}
              </div>
              <div className="flex items-center gap-2">
                <span className={`rounded-full px-2.5 py-1 text-[9px] font-bold uppercase tracking-[0.12em] ${COLORS[v.status] || COLORS.new}`}>{v.status}</span>
                <button onClick={() => remove(v.id)} aria-label="Delete" data-testid={`re-viewing-delete-${v.id}`} className="flex h-8 w-8 items-center justify-center rounded-full border border-red-200 text-red-500 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {STATUS.map((s) => (
                <button key={s} onClick={() => setStatus(v.id, s)} data-testid={`re-viewing-${v.id}-status-${s}`}
                  className={`rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.1em] transition-colors ${v.status === s ? "bg-[#1A1615] text-[#F3E5AB]" : "border border-[#E5D9CF] text-[#5A504B] hover:bg-[#F5EBE6]"}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="glass rounded-2xl p-6 text-sm font-light text-[#91837C]">No viewing requests yet.</p>}
      </div>
    </div>
  );
};
